import { useCallback, useEffect, useReducer, useRef } from "react";
import useState from "react-usestateref";
import { Model, useEffectAsync } from "@alphascriptsolutions/client-core";
import Renderer from "./Renderer";
import { Button, DatePicker, Input, message, Modal, Select, Tooltip, Typography, Spin } from "antd";
import dayjs from "dayjs";
import * as yup from "yup";

import TagsInputField from "./TagsInputField";
import { Company } from "@/state/entities";
import { CloseOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { ModelIndexes, ModelsCount } from "@/types/addContent";
import {
  getAllImageButtonValues,
  getNextPanel,
  setAllModelNavigationBtns,
} from "@/utils/sharePricePerformance/addPanelsTransition";

import { isEmptyObject } from "@/utils/sharePricePerformance/panelHelpers";
import { getSharePricePerformanceData } from "@/utils/sharePricePerformance/getSharePricePerformanceData";
import { useSharePricePerformanceStore } from "@/state/sharePricePerformanceStore";
import useFetchCurrencies from "../utils/useFetchCurrencies";
import { findCompanyByName, setCompanies } from "@/utils/sharePricePerformance/companyDetailsLocalStorage";
import { applyOldModelState } from "@/utils/sharePricePerformance/applyOldModelState";
import { addEventsOnModel } from "@/utils/sharePricePerformance/addEventsOnModels";
import { bufferToBase64ImagePrimaryTemplate } from "@/utils/formatTemplate/slides-extraction-handlers";
import { logger } from "@/helpers/tslog";
import {
  updateTableDatasource,
  updateAPALTableDatasource,
  updateCallOutBoxesTableDatasource,
  updateShadingPeriodTableDatasource,
  updateSmallLineChartData,
  updateTestTableDatasource,
  updateVerticalLineTableDatasource,
} from "@/utils/sharePricePerformance/updatePanelTables";
import FormatTemplateSearchPopup from "@/alphaPopup/components/FormatTemplateSearchPopup";
const { RangePicker } = DatePicker;

export function SharePricePerformancePanels({ setIsFirstStep }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    currentStep,
    formCompleted,
    errors,
    company,
    models,
    timePeriod,
    openModelType,
    summaryTable,
    shadingPeriodTable,
    verticalLineTable,
    callOutBoxTable,
    APALCompany,
    APALCompanyIndex,
    isLoaded,
    templateSearchVisible,
    currentPrimaryTemplate,
    setErrors,
    setCompany,
    setCurrencyOptions,
    setModels,
    setCurrentStep,
    setOpenModelType,
    setSummaryTable,
    setShadingPeriodTable,
    setVerticalLineTable,
    setCallOutBoxTable,
    setAPALCompany,
    setIsLoaded,

    setTemplateSearchVisible,
    setCurrentPrimaryTemplate,
  } = useSharePricePerformanceStore();

  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const { fetchCurrencies } = useFetchCurrencies();
  const [modalDataState, setModalDataState] = useState({
    periodName: "",
    label: "",
    dateRange: [] as [string, string] | [],
    date: "",
    callOutBoxCompany: company?.[0]?.companyNameAndTicker || "",
    text: "",
  });

  const updateFormState = (key: string, value: any) => {
    setModalDataState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  useEffectAsync(async () => {
    const currencyOptions = await fetchCurrencies();
    if (currencyOptions) setCurrencyOptions(currencyOptions);

    const paths = [
      "/assets/ContentLibrary.yml",
      "/assets/InitialInputs.yml",
      "/assets/Indexation.yml",
      "/assets/CustomBuildIndex.yml",
      "/assets/StaticLine.yml",
      "/assets/Customize.yaml",
      "/assets/customization_inputs/SummaryTable.yml",
      "/assets/customization_inputs/SmallLineChart.yml",
      "/assets/customization_inputs/CallOutBoxes.yml",
      "/assets/customization_inputs/BackgroundShading.yml",
      "/assets/customization_inputs/VerticalLine.yml",
      "/assets/FormatTemplate.yml",
    ];

    try {
      // Load all models concurrently.
      const { dataSource, oldModelsData } = getSharePricePerformanceData();
      let loadedModels = await Promise.all(
        paths.map(async (path, index) => {
          const yamlData = await Model.getYaml(path);
          let modelInstance: Model;
          const hasValidData = oldModelsData?.[index] && !isEmptyObject(oldModelsData[index]);

          if (hasValidData) {
            modelInstance = new Model(yamlData, dataSource[index]);
            applyOldModelState(index, oldModelsData[index]);
          } else {
            modelInstance = new Model(yamlData);
          }

          if (!modelInstance.isValid()) {
            throw new Error(`Problem loading model from ${path}: ${modelInstance.getError()}`);
          }
          return modelInstance;
        })
      );

      setAllModelNavigationBtns(loadedModels, handleBack, handleNext, handleClosePopup);

      const loadModelsWithEvents = async () => {
        return await Promise.all(loadedModels.map((model, index) => addEventsOnModel(model, index, forceUpdate)));
      };

      let loadedModelsWithEvents = await loadModelsWithEvents();
      /**
       * 🔄 Transition Logic: Chat → Format Template Step
       * ------------------------------------------------
       * if `changeWindow` flag is set in localStorage (from chat response),
       * This flag is set from the chat when the conversation ends (on "done" message sent to backend).
       * we move to the FORMAT_TEMPLATE step where the user selects
       * placeholder positions to push the chart.
       *
       * ⚠️ Note: This is a temporary check to complete the flow.
       * Proper logic will be added later to ensure a consistent
       * and validated transition based on actual data states.
       */
      const changeWindow = localStorage.getItem("changeWindow");
      if ((dataSource && oldModelsData.length) || changeWindow === "true") {
        loadedModelsWithEvents = showCurrentStep(loadedModelsWithEvents);
        localStorage.removeItem("changeWindow");
        setTimeout(() => {
          setIsLoaded(true);
        }, 100);
      } else {
        setIsLoaded(true);
      }

      setModels(loadedModelsWithEvents);
    } catch (err) {
      message.error(err.message);
    }
  }, []);

  useEffect(() => {
    if (models.length) showCurrentStep(undefined);

    if (panelRef.current) {
      setTimeout(() => {
        const panelElement = panelRef.current.querySelector(`.panel-${currentStep + 1}`);
        if (panelElement) {
          window.scrollTo({
            top: window.scrollY + panelElement.getClientRects()[0].y,
            behavior: "smooth",
          });
        }
      }, 600);
    }
    setIsFirstStep(() => currentStep === ModelIndexes.CONTENT_LIBRARY);
  }, [currentStep]);

  useEffect(() => {
    if (models.length === 0) return;
    if (company.length) updateFormState("callOutBoxCompany", company[0].companyNameAndTicker);

    try {
      setModels((prevModels) => {
        let changed = false;
        const newModels = prevModels.map((model, index) => {
          if (index === ModelIndexes.TABLE_MODEL_INDEX) {
            const tableChanged = updateTableDatasource(model);
            changed = changed || tableChanged;
          }
          if (index === ModelIndexes.CUSTOM_BUILD_INDEX) {
            const tableChanged = updateAPALTableDatasource(model);
            changed = changed || tableChanged;
          }
          if (index === ModelIndexes.SUMMARY_TABLE) {
            const testTableUpdate = updateTestTableDatasource(model);
            changed = changed || testTableUpdate;
          }
          if (index === ModelIndexes.SMALL_LINE_CHART) {
            const snallLineChartValuesUpdate = updateSmallLineChartData(model);
            changed = changed || snallLineChartValuesUpdate;
          }
          if (index === ModelIndexes.CALL_OUT_BOXES) {
            const callOutBoxChanged = updateCallOutBoxesTableDatasource(model);
            changed = changed || callOutBoxChanged;
          }
          if (index === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL) {
            const shadingPeriodTableChanged = updateShadingPeriodTableDatasource(model);
            changed = changed || shadingPeriodTableChanged;
          }
          if (index === ModelIndexes.VERTICAL_LINE_TABLE_PANEL) {
            const verticalLineChanged = updateVerticalLineTableDatasource(model);
            changed = changed || verticalLineChanged;
          }

          return model;
        });
        return changed ? newModels : prevModels;
      });
    } catch (err) {
      message.error(err.message);
    }
  }, [company, APALCompany, summaryTable, shadingPeriodTable, verticalLineTable, callOutBoxTable, currentStep]);

  const setErrorOnImageButtons = async (errors: yup.ValidationError[], panelIndex: number) => {
    const { models } = useSharePricePerformanceStore.getState();
    const model = await models[panelIndex];
    errors.forEach((error) => {
      if (!error.type.startsWith("object-")) return;
      const fieldsName = error.type.split("object-")[1];
      const imageButton = model.getByName(fieldsName);
      imageButton.error = error.message;
    });

    setModels((prev) => {
      const newModels = [...prev];
      newModels.splice(panelIndex, 1, model);
      return newModels;
    });
  };

  const handleNext = async (buttonPanelIndex: number) => {
    const { currentStep, models } = useSharePricePerformanceStore.getState();
    const e = await models[buttonPanelIndex].validate();
    if (e.length > 0) {
      setErrors(e);
      await setErrorOnImageButtons(e, buttonPanelIndex);
      return;
    }

    const nextStep = getNextPanel({ currentPanel: buttonPanelIndex, direction: "next", models: models });
    setCurrentStep(nextStep);

    setIsFirstStep(() => currentStep === ModelIndexes.CONTENT_LIBRARY);
  };

  const handleBack = useCallback((buttonPanelIndex: number) => {
    const { currentStep, models } = useSharePricePerformanceStore.getState();
    const nextStep = getNextPanel({ currentPanel: buttonPanelIndex, direction: "back", models: models });
    setCurrentStep(nextStep);

    setIsFirstStep(() => currentStep === ModelIndexes.CONTENT_LIBRARY);
  }, []);

  const handleClosePopup = useCallback(() => Office.context.ui.messageParent("CLOSE"), []);

  const showCurrentStep = (models) => {
    const { currentStep } = useSharePricePerformanceStore.getState();

    if (models) {
      return models.map((model, index) => {
        const currentComponent = model.uiComponents[0];
        const shouldBeActive = index <= currentStep;
        model.uiComponents[0].panelOrder = index + 1;
        model.uiComponents[0].selectedPanel = currentStep + 1;
        if (currentComponent.isEnabled !== shouldBeActive) {
          model.uiComponents[0].isEnabled = shouldBeActive;
        }

        return model;
      });
    } else {
      setModels((prevModels) => {
        return prevModels.map((model, index) => {
          const currentComponent = model.uiComponents[0];
          const shouldBeActive = index <= currentStep;
          model.uiComponents[0].panelOrder = index + 1;
          model.uiComponents[0].selectedPanel = currentStep + 1;
          if (currentComponent.isEnabled !== shouldBeActive) {
            model.uiComponents[0].isEnabled = shouldBeActive;
          }

          return model;
        });
      });
    }
  };

  const handleOk = () => {
    if (openModelType === "periodSummaryTable") {
      if (!modalDataState.periodName || modalDataState.dateRange.length !== 2) return;
    } else if (openModelType === "verticalLineTable") {
      if (!modalDataState.label || !modalDataState.date) return;
      if (verticalLineTable.length >= 3) {
        message.error("You have reached the limit of 3 companies.");
        return;
      }
    } else if (openModelType === "ShadingPeriodTable") {
      if (!modalDataState.label || modalDataState.dateRange.length !== 2) return;
      if (shadingPeriodTable.length >= 3) {
        message.error("You have reached the limit of 3 companies.");
        return;
      }
    } else if (openModelType === "CallOutBox") {
      if (!modalDataState.date || !modalDataState.text || !modalDataState.callOutBoxCompany) return;
      if (callOutBoxTable.length >= 5) {
        message.error("You have reached the limit of 5 companies.");
        return;
      }
    }

    // Mapping modal types to their corresponding update functions
    const updateFunctions: Record<string, () => void> = {
      periodSummaryTable: () => {
        const tableData = [
          ...summaryTable,
          {
            id: summaryTable.length ? summaryTable[summaryTable.length - 1].id + 1 : 1,
            periodName: modalDataState.periodName,
            dateRange: (modalDataState.dateRange as [string, string]).join(" -> "),
          },
        ];
        setSummaryTable(tableData);
      },
      ShadingPeriodTable: () => {
        const tableData = [
          ...shadingPeriodTable,
          {
            id: shadingPeriodTable.length ? shadingPeriodTable[shadingPeriodTable.length - 1].id + 1 : 1,
            label: modalDataState.label,
            dateRange: (modalDataState.dateRange as [string, string]).join(" -> "),
          },
        ];
        setShadingPeriodTable(tableData);
      },
      verticalLineTable: () => {
        const tableData = [
          ...verticalLineTable,
          {
            id: verticalLineTable.length ? verticalLineTable[verticalLineTable.length - 1].id + 1 : 1,
            label: modalDataState.label,
            date: modalDataState.date,
          },
        ];
        setVerticalLineTable(tableData);
      },
      CallOutBox: () => {
        const companyData: Company = findCompanyByName(modalDataState.callOutBoxCompany);
        const tableData = [
          ...callOutBoxTable,
          {
            id: callOutBoxTable.length ? callOutBoxTable[callOutBoxTable.length - 1].id + 1 : 1,
            line: companyData.companyNameAndTicker,
            text: modalDataState.text,
            date: modalDataState.date,
            company: {
              ticker: companyData.company.ticker,
              exchange: companyData.company.exchange,
            },
          },
        ];
        setCallOutBoxTable(tableData);
      },
    };

    updateFunctions[openModelType!]?.();

    setModalDataState({
      periodName: "",
      label: "",
      dateRange: [] as [string, string] | [],
      date: "",
      callOutBoxCompany: "",
      text: "",
    });
    setOpenModelType(null);
  };

  const handleCancel = () => {
    setModalDataState({
      periodName: "",
      label: "",
      dateRange: [] as [string, string] | [],
      date: "",
      callOutBoxCompany: "",
      text: "",
    });
    setOpenModelType(null);
  };

  const handleModalInputChange = (value: string) => {
    const { company } = useSharePricePerformanceStore.getState();
    if (company.length >= 7) {
      message.error("You have reached the limit of 7 companies.");
      return;
    }
    const selectedCompany = JSON.parse(value);
    const idnew = company?.length ? company[company?.length - 1].id + 1 : 1;
    const newCompanyAdded = {
      id: company.length ? company[company.length - 1].id + 1 : 1,
      lineCategory: company.length === 0 ? "Primary Company" : "Peer Company",
      companyNameAndTicker: selectedCompany.name,
      dataType: `Closing Share Price-${idnew}`,
      companyId: selectedCompany.symbol,
      company: {
        ticker: selectedCompany.symbol.split(":")[1],
        exchange: selectedCompany.symbol.split(":")[0],
      },
    };
    const companiesData: Company[] = [...company, newCompanyAdded];
    setCompany(companiesData);
    setCompanies(companiesData);
  };

  const handleChangeAPALCompanyInput = (value: string) => {
    if (APALCompany[APALCompanyIndex]?.length >= 10) {
      message.error("You have reached the limit of 10 companies.");
      return;
    }
    const selectedCompany = JSON.parse(value);
    const newState = [...APALCompany];
    const currentTableIndex = APALCompanyIndex;
    newState[currentTableIndex] ??= [];
    const currentTable = newState[currentTableIndex];

    let lastNumber = 0;
    if (currentTable.length > 0) {
      const lastId = currentTable[currentTable.length - 1].hiddenId; // e.g., "2-3"
      const parts = lastId.split("-");
      if (parts.length > 1) lastNumber = parseInt(parts[1], 10);
    }
    const newNumber = lastNumber + 1;

    currentTable.push({
      id: `${newNumber}`,
      companyNameAndTicker: selectedCompany.name,
      removeCompany: `${currentTableIndex}-${newNumber}`,
      company: {
        ticker: selectedCompany.symbol.split(":")[1],
        exchange: selectedCompany.symbol.split(":")[0],
      },
      hiddenId: `${currentTableIndex}-${newNumber}`,
    });

    setAPALCompany(newState);
  };

  const handleModalTextChange = (e) => {
    const inputText = e.target.value;
    const wordCount = inputText.trim().split(/\s+/).filter(Boolean).length;

    if (wordCount > 30) {
      message.error("Maximum 30 words allowed");
    } else {
      updateFormState("text", inputText);
    }
  };

  if (models.length !== ModelsCount && !isLoaded)
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "10px",
        }}
      >
        <Spin size="large" tip="Loading" />
        <Typography.Text>Loading</Typography.Text>
      </div>
    );
  const companyOptions = company?.map((c) => ({
    value: c.companyNameAndTicker,
    label: c.companyNameAndTicker,
  }));

  const handleTemplateSelect = async (selectedTemplate: any) => {
    try {
      logger.info(`Template selected: ${selectedTemplate.formatName}`);

      const presentationDesign = models[currentStep]?.getByName("presentationDesign");
      if (presentationDesign && selectedTemplate) {
        if (selectedTemplate.formatName) {
          (presentationDesign as any).templateName = selectedTemplate.formatName;
        }
        (presentationDesign as any).value = selectedTemplate.id;
        if (selectedTemplate.frontCoverImage?.buffer) {
          try {
            const getImageSrc = bufferToBase64ImagePrimaryTemplate(
              selectedTemplate.frontCoverImage.buffer,
              selectedTemplate.frontCoverImage.contentType
            );
            (presentationDesign as any).image = getImageSrc;
          } catch (conversionError) {
            logger.error("Error converting image:", conversionError);
            (presentationDesign as any).image = undefined;
          }
        } else {
          (presentationDesign as any).image = undefined;
        }

        setCurrentPrimaryTemplate(selectedTemplate);
        setModels(models); // Update the models in the store

        // Store the selected template in localStorage for persistence
        localStorage.setItem(
          "selectedFormatTemplate",
          JSON.stringify({
            id: selectedTemplate.id,
            formatName: selectedTemplate.formatName,
            image: selectedTemplate.frontCoverImage?.buffer
              ? bufferToBase64ImagePrimaryTemplate(
                  selectedTemplate.frontCoverImage.buffer,
                  selectedTemplate.frontCoverImage.contentType
                )
              : undefined,
          })
        );

        logger.info("Template updated successfully in presentation design and saved to localStorage");
      }
    } catch (error) {
      logger.error("Error handling template selection:", error);
    }
  };

  return (
    <div ref={panelRef} style={{ paddingBottom: "300px", height: "100%", boxSizing: "border-box" }}>
      <FormatTemplateSearchPopup
        visible={templateSearchVisible}
        onClose={() => setTemplateSearchVisible(false)}
        onTemplateSelect={handleTemplateSelect}
        currentPrimaryTemplateId={currentPrimaryTemplate?.id}
      />
      <Modal
        title={
          <div
            style={{
              backgroundColor: "#002945",
              color: "#ffffff",
              padding: "20px 24px 10px 24px",
              margin: "-20px -24px 0 -24px",
              borderRadius: "8px 8px 0 0",
            }}
          >
            {(openModelType === "company" && "Add Company") ||
              (openModelType === "APALCompany" && "Add Company") ||
              (openModelType === "periodSummaryTable" && "Period Input Selections") ||
              (openModelType === "CallOutBox" && "Call Out Box Table") ||
              (openModelType === "ShadingPeriodTable" && "Add Shading Period") ||
              (openModelType === "verticalLineTable" && "Add Vertical Line")}
          </div>
        }
        cancelText={"Close"}
        okText={"Add to Table"}
        open={!!openModelType}
        onOk={handleOk}
        onCancel={handleCancel}
        closeIcon={<CloseOutlined style={{ color: "#ffffff" }} />}
      >
        {openModelType === "company" && (
          <TagsInputField
            key={openModelType + company}
            onChange={(tags: any) => handleModalInputChange(tags)}
            value={company}
          />
        )}
        {openModelType === "APALCompany" && (
          <TagsInputField
            key={openModelType + APALCompany[APALCompanyIndex]}
            value={APALCompany[APALCompanyIndex]}
            onChange={(tags: any) => handleChangeAPALCompanyInput(tags)}
          />
        )}
        {openModelType === "periodSummaryTable" && (
          <div>
            <div>
              <Typography.Title level={5}>Period Name</Typography.Title>
              <Input
                placeholder="Enter Period Name"
                value={modalDataState.periodName}
                onChange={(e) => updateFormState("periodName", e.target.value)}
              />
            </div>
            <div>
              <Typography.Title level={5}>Date (Bgn - End)</Typography.Title>
              <RangePicker
                disabledDate={(current) => {
                  if (!timePeriod) return false;
                  const [start, end] = timePeriod;
                  return current && (current.isBefore(dayjs(start), "day") || current.isAfter(dayjs(end), "day"));
                }}
                onChange={(_, dateStrings) => updateFormState("dateRange", dateStrings as [string, string])}
                value={
                  modalDataState.dateRange.length === 2
                    ? [dayjs(modalDataState.dateRange[0]), dayjs(modalDataState.dateRange[1])]
                    : undefined
                }
              />
            </div>
          </div>
        )}
        {openModelType === "ShadingPeriodTable" && (
          <div>
            <div>
              <Typography.Title level={5}>Label Name On Chart</Typography.Title>
              <Input
                placeholder="Enter Label"
                value={modalDataState.label}
                onChange={(e) => updateFormState("label", e.target.value)}
              />
            </div>
            <div>
              <Typography.Title level={5}>Date (Bgn - End)</Typography.Title>
              <RangePicker
                onChange={(_, dateStrings) => updateFormState("dateRange", dateStrings as [string, string])}
                value={
                  modalDataState.dateRange.length === 2
                    ? [dayjs(modalDataState.dateRange[0]), dayjs(modalDataState.dateRange[1])]
                    : undefined
                }
                disabledDate={(current) => {
                  if (!timePeriod) return false;
                  const [start, end] = timePeriod;
                  return current && (current.isBefore(dayjs(start), "day") || current.isAfter(dayjs(end), "day"));
                }}
              />
            </div>
          </div>
        )}
        {openModelType === "verticalLineTable" && (
          <div>
            <div>
              <Typography.Title level={5}>Label Name On Chart</Typography.Title>
              <Input
                placeholder="Enter Label"
                value={modalDataState.label}
                onChange={(e) => updateFormState("label", e.target.value)}
              />
            </div>
            <div>
              <Typography.Title level={5}>Date</Typography.Title>
              <DatePicker
                onChange={(_, dateString) => updateFormState("date", dateString as string)}
                value={modalDataState.date ? dayjs(modalDataState.date) : undefined}
                disabledDate={(current) => {
                  if (!timePeriod) return false;
                  const [start, end] = timePeriod;
                  return current && (current.isBefore(dayjs(start), "day") || current.isAfter(dayjs(end), "day"));
                }}
              />
            </div>
          </div>
        )}
        {openModelType === "CallOutBox" && (
          <div>
            <div>
              <Typography.Title level={5}>
                Line{" "}
                <Tooltip
                  placement="top"
                  title={"Select the line on the chart that you want the call out box to emanate from."}
                >
                  <InfoCircleOutlined style={{ cursor: "pointer" }} />
                </Tooltip>
              </Typography.Title>

              {companyOptions.length > 0 ? (
                <Select
                  value={modalDataState.callOutBoxCompany}
                  style={{ width: 120 }}
                  options={companyOptions}
                  onChange={(value) => updateFormState("callOutBoxCompany", value)}
                />
              ) : (
                <div>No company was selected during the Initial Input step.</div>
              )}
            </div>

            <div>
              <Typography.Title level={5}>
                Date{" "}
                <Tooltip
                  placement="top"
                  title={"Select the date on the chart you want the call out box to emanate from."}
                >
                  <InfoCircleOutlined style={{ cursor: "pointer" }} />
                </Tooltip>
              </Typography.Title>
              <DatePicker
                onChange={(_, dateString) => updateFormState("date", dateString as string)}
                value={modalDataState.date ? dayjs(modalDataState.date) : undefined}
                disabledDate={(current) => {
                  if (!timePeriod) return false;
                  const [start, end] = timePeriod;
                  return current && (current.isBefore(dayjs(start), "day") || current.isAfter(dayjs(end), "day"));
                }}
              />
            </div>
            <div>
              <Typography.Title level={5}>
                Text{" "}
                <Tooltip placement="top" title={"Phrase you would like written in the call out box."}>
                  <InfoCircleOutlined style={{ cursor: "pointer" }} />
                </Tooltip>
              </Typography.Title>
              <Input placeholder="Write the Text" onChange={handleModalTextChange} value={modalDataState.text} />
            </div>
          </div>
        )}
      </Modal>

      {!formCompleted &&
        models.map((model, i) => {
          // Step 0: Show only Panel 0
          if (currentStep === 0) return i === 0 ? <Renderer key={i} model={model} errors={errors} /> : null;

          const {
            isValidIndexation,
            isCustomBuiltIndice,
            isConstantLine,
            isValidSummaryTable,
            isValidSmallLineChart,
            isValidCallOutBoxes,
            isValidBackgroundShading,
            isValidVerticalLine,
          } = getAllImageButtonValues(models);

          // Step 1: Show all panels except Panel 4
          if (
            // (currentStep === ModelIndexes.TABLE_MODEL_INDEX || currentStep === ModelIndexes.CUSTOMIZE_OPTIONS) &&
            currentStep === ModelIndexes.TABLE_MODEL_INDEX &&
            (i === ModelIndexes.INDEXATION_MODEL_INDEX ||
              i === ModelIndexes.CUSTOM_BUILD_INDEX ||
              i === ModelIndexes.CONSTANT_LINE ||
              i === ModelIndexes.SUMMARY_TABLE ||
              i === ModelIndexes.SMALL_LINE_CHART ||
              i === ModelIndexes.CALL_OUT_BOXES ||
              i === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL ||
              i === ModelIndexes.VERTICAL_LINE_TABLE_PANEL)
          )
            return null;

          if (
            (currentStep === ModelIndexes.INDEXATION_MODEL_INDEX ||
              currentStep === ModelIndexes.CUSTOM_BUILD_INDEX ||
              currentStep === ModelIndexes.CONSTANT_LINE ||
              currentStep === ModelIndexes.CUSTOMIZE_OPTIONS) &&
            (i === ModelIndexes.SUMMARY_TABLE ||
              i === ModelIndexes.SMALL_LINE_CHART ||
              i === ModelIndexes.CALL_OUT_BOXES ||
              i === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL ||
              i === ModelIndexes.VERTICAL_LINE_TABLE_PANEL)
          )
            return null;

          // This feels like a place where we could use a state machine (npm: xstate) for tracking state transitions
          // Ideally, it should add support into the client-core

          // Step > 1: Show all panels, include/exclude based on condition
          if (currentStep > 1 && i === ModelIndexes.INDEXATION_MODEL_INDEX && isValidIndexation !== "Yes") return null;
          if (currentStep > 1 && i === ModelIndexes.CUSTOM_BUILD_INDEX && isCustomBuiltIndice !== "Yes") return null;
          if (currentStep > 1 && i === ModelIndexes.CONSTANT_LINE && isConstantLine !== "Yes") return null;

          if (currentStep > 5 && i === ModelIndexes.SUMMARY_TABLE && isValidSummaryTable !== "Yes") return null;
          if (currentStep > 5 && i === ModelIndexes.SMALL_LINE_CHART && isValidSmallLineChart !== "Yes") return null;
          if (currentStep > 5 && i === ModelIndexes.CALL_OUT_BOXES && isValidCallOutBoxes !== "Yes") return null;
          if (
            currentStep > 5 &&
            i === ModelIndexes.BACKGROUND_SHADING_TABLE_PANEL &&
            isValidBackgroundShading !== "Yes"
          )
            return null;
          if (currentStep > 5 && i === ModelIndexes.VERTICAL_LINE_TABLE_PANEL && isValidVerticalLine !== "Yes")
            return null;

          return <Renderer key={i} model={model} errors={errors} />;
        })}

      {formCompleted && (
        <div
          style={{
            boxSizing: "border-box",
            padding: "0 50px",
            fontWeight: "bold",
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <p className="text-gray-800">
            Thank you. We are working on the Excel model and placeholder content you requested. We estimate this to take
            1 – 2 minutes. Your Taskpane will open when the content is ready.
          </p>
          <Button
            type="primary"
            size="large"
            onClick={() => Office.context.ui.messageParent("CLOSE")}
            className="w-full"
          >
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
